// --- Validation rules and runner for flow relations ---
// This file centralizes all validation logic for flow relations.

import { renderFlowItemType } from "../helpers/typedHelpers";

// --- Validation rule constants (duplicated here to break circular dependency) ---
// Re-export validation rule arrays for external usage (for legacy and compatibility)
export const cannotHaveChild = [
    "toCloud",
    "report",
    "model",
    "singleview",
    "insights",
    "dataload",
    "discoveryinsights",
    "audienceinsights",
    "cannedreport",
];
export const cannotHaveParent = ["fromCloud"];
export const childMustBeEmpty = ["split", "case"];
export const allowsMultipleParents = [
    "merge",
    "offerMerge",
    "model",
    "export",
    "script",
    "scriptdbui",
    "flowExpression",
    "multiexport",
];

// --- Unified Validation Rule Structure ---
// All validation rules are now defined as objects in a single array, with a common interface:
// { id, description, test: (args) => boolean, message: string|function, allowed?: boolean }
// Each rule's test function receives: { parentType, childType, childNumParents, skipMultiParentCheck }

export const FLOW_RELATION_VALIDATION_RULES = [
    {
        id: "cannot-have-child",
        description: "Certain types cannot have children.",
        test: ({ parentType }) => cannotHaveChild.includes(parentType),
        message: ({ parentType }) => `${renderFlowItemType(parentType)} cannot have child items.`,
    },
    {
        id: "cannot-have-parent",
        description: "Certain types cannot have parents.",
        test: ({ childType }) => cannotHaveParent.includes(childType),
        message: ({ childType }) => `${renderFlowItemType(childType)} cannot have parent items.`,
    },
    {
        id: "child-must-be-empty",
        description: "Split and Case can only have generic items as child.",
        test: ({ parentType, childType }) => childMustBeEmpty.includes(parentType) && childType !== "empty",
        message: () => "Split and Case can only have generic items as child.",
    },
    {
        id: "multiple-parents",
        description: "Multiple parents are not allowed for this item.",
        test: ({ childType, childNumParents, skipMultiParentCheck }) =>
            !skipMultiParentCheck && childNumParents > 0 && !allowsMultipleParents.includes(childType),
        message: () => "Multiple parents are not allowed for this item.",
    },
    {
        id: "multi-export",
        description: "MultiExports cannot have child items.",
        test: ({ parentType, childType }) => parentType === "multiexport" && childType !== "export",
        message: () => "MultiExports cannot have child items.",
    },
    {
        id: "export-script",
        description: "Export can have Script or ScriptDBUI as children (allowed).",
        test: ({ parentType, childType }) =>
            parentType === "export" && (childType === "script" || childType === "scriptdbui"),
        message: () => "",
        allowed: true,
    },
    {
        id: "export-children",
        description: "Exports cannot have child items except certain types.",
        test: ({ parentType, childType }) =>
            parentType === "export" &&
            !["exportreport", "toCloud", "output", "merge", "script", "scriptdbui", "cannedreport"].includes(childType),
        message: () => "Exports cannot have child items.",
    },
    {
        id: "flow-expression",
        description: "Expression items may only have Script items as children.",
        test: ({ parentType, childType }) =>
            parentType === "flowExpression" && !["script", "scriptdbui"].includes(childType),
        message: () => "Expression items may only have Script items as children.",
    },
    {
        id: "output-parent",
        description: "Expression items cannot have an Output item as parent.",
        test: ({ parentType, childType }) => parentType === "output" && childType === "flowExpression",
        message: () => "Expression items cannot have an Output item as parent",
    },
    {
        id: "offload",
        description: "Offload items may only have Script or Offload items as children.",
        test: ({ parentType, childType }) =>
            parentType === "offload" && !["script", "scriptdbui", "offload"].includes(childType),
        message: () => "Offload items may only have Script or Offload items as children.",
    },
    {
        id: "output-child",
        description: "Output items can only be children of Export, Expression or Script items.",
        test: ({ parentType, childType }) =>
            childType === "output" && !["export", "script", "scriptdbui"].includes(parentType),
        message: () => "Output items can only be children of Export, Expression or Script items.",
    },
    {
        id: "single-view",
        description: "SingleView cannot have child items.",
        test: ({ parentType }) => parentType === "singleview",
        message: () => "SingleView cannot have child items.",
    },
    {
        id: "export-report",
        description: "Export reports can only be children of an Export item.",
        test: ({ parentType, childType }) => childType === "exportreport" && parentType !== "export",
        message: () => "Export reports can only be children of an Export item.",
    },
    {
        id: "script-export",
        description: "Script items cannot be a parent to an Export item.",
        test: ({ parentType, childType }) => childType === "export" && parentType === "script",
        message: () => "Script items cannot be a parent to an Export item.",
    },
    {
        id: "offer-merge",
        description: "Offers may only have types Report, Export, Dedupe, and To Cloud as children.",
        test: ({ parentType, childType }) =>
            parentType === "offerMerge" &&
            !["report", "multiexport", "export", "svDedupe", "toCloud"].includes(childType),
        message: () => "Offers may only have types Report, Export, Dedupe, and To Cloud as children.",
    },
    // --- Add more rules as needed ---
];

// --- Unified Validation Runner ---
/**
 * Runs validation rules with configurable options.
 * @param {Object} options
 * @param {string} parentType
 * @param {string} childType
 * @param {number} childNumParents
 * @param {boolean} skipMultiParentCheck
 * @param {boolean} notifyOnFail - If true, dispatches notification on failure.
 * @param {function} notifyFn - Optional notification function (e.g., top.store.dispatch)
 * @returns {Object} Validation result: { allowed, message, ruleId? }
 */
export function unifiedValidationRunner({
    parentType,
    childType,
    childNumParents = 0,
    skipMultiParentCheck = false,
    notifyOnFail = false,
    notifyFn = null,
}) {
    for (const rule of FLOW_RELATION_VALIDATION_RULES) {
        if (rule.test({ parentType, childType, childNumParents, skipMultiParentCheck })) {
            const msg = typeof rule.message === "function" ? rule.message({ parentType, childType }) : rule.message;
            const result = { allowed: rule.allowed === true, message: msg, ruleId: rule.id };
            if (!result.allowed && notifyOnFail && typeof notifyFn === "function") {
                notifyFn(msg);
            }
            return result;
        }
    }
    return { allowed: true, message: "" };
}

// --- Legacy API wrappers for compatibility ---
export function runValidationRules(parent, child, childNumParents, shouldSkipMultipleParentCheck) {
    const result = unifiedValidationRunner({
        parentType: parent,
        childType: child,
        childNumParents,
        skipMultiParentCheck: shouldSkipMultipleParentCheck,
    });
    return result.allowed ? null : result;
}

// Removed the pureRunValidationRules function as it was redundant.

export function runValidationRulesWithNotify(parent, child, childNumParents, shouldSkipMultipleParentCheck) {
    const result = unifiedValidationRunner({
        parentType: parent,
        childType: child,
        childNumParents,
        skipMultiParentCheck: shouldSkipMultipleParentCheck,
        notifyOnFail: true,
        notifyFn: msg => top.store.dispatch(msg),
    });
    return result.allowed ? null : result;
}

// --- Relation type rules ---
export const relationTypeRules = [
    {
        child: "insights",
        parent: ["filter"],
        message: "Insights can only be children of Filter items.",
    },
    {
        child: "dataload",
        parent: ["script", "scriptdbui"],
        message: "Data Load can only be children of a Script item.",
    },
    {
        child: ["script", "scriptdbui"],
        parent: ["script", "scriptdbui", "offload", "flowExpression"],
        message: "Script can only be children of a Script, Script DB-UI, Expression or Offload item.",
    },
    {
        child: "cannedreport",
        parent: ["export", "multiexport", "script", "scriptdbui"],
        message: "Canned Report can only be children of a Script or Export item.",
    },
    {
        child: "discoveryinsights",
        parentNot: ["script", "scriptdbui", "multiexport", "export", "output", "exportreport"],
        message: "Audience Insights has a restricted list of parents.",
    },
    // ...add more rules here as needed...
];

/**
 * Validates relation type rules and returns the first failure or null if all pass.
 */
// --- Helper: Checks if a rule matches the given child type ---
function ruleChildMatch(rule, childItemType) {
    if (Array.isArray(rule.child)) return rule.child.includes(childItemType);
    return rule.child === childItemType;
}

// --- Helper: Checks if a parent type is valid for a rule ---
function ruleParentValid(rule, parentItemType) {
    if (rule.parent) return rule.parent.includes(parentItemType);
    if (rule.parentNot) return !rule.parentNot.includes(parentItemType);
    return true;
}

export function validateRelationTypeRule(childItemType, parentItemType) {
    for (const rule of relationTypeRules) {
        if (!ruleChildMatch(rule, childItemType)) continue;
        if (!ruleParentValid(rule, parentItemType)) {
            return { allowed: false, message: rule.message };
        }
    }
    return null;
}
